import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../utils/axiosInstance';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import RelatedItems from './common/ReelatedItems';
import { useNavigate } from 'react-router-dom';

const ItemsNeededByDate = ({ defaultDate }) => {
  const initialDate = useMemo(() => {
    if (!defaultDate) return null;
    
    // Handle string dates
    if (typeof defaultDate === 'string') {
      const [year, month, day] = defaultDate.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    return defaultDate;
  }, [defaultDate]);

  const [date, setDate] = useState(initialDate);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (initialDate) fetchItems(initialDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate]);

  const fetchItems = async (selectedDate) => {
    if (!selectedDate) {
      setError('Please select a date');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Format date as YYYY-MM-DD in local timezone
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      const res = await axiosInstance.get('/order/items-needed/by-date', {
        params: { date: formattedDate },
      });

      setItems(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch items');
    }

    setLoading(false);
  };

  const handleDateChange = (e) => {
    if (!e.target.value) {
      setDate(null);
      return;
    }
    
    // Parse as local date to avoid timezone issues
    const [year, month, day] = e.target.value.split('-').map(Number);
    const newDate = new Date(year, month - 1, day); // month is 0-indexed
    setDate(newDate);
  };

  // Column definitions for inventory summary
  const statusColor = (status) => {
    const s = String(status || '').toLowerCase();
    const m = {
      pending: 'warning',
      confirmed: 'info',
      delivered: 'primary',
      cancelled: 'error',
      completed: 'success',
    };
    return m[s] || 'default';
  };

  const inventoryColumns = [
    {
      key: 'name',
      label: 'Item Name',
      render: (value) => (
        <Typography component="span" variant="body2" fontWeight={600}>
          {value}
        </Typography>
      ),
    },
    {
      key: 'totalQuantityNeeded',
      label: 'Quantity needed',
      align: 'center',
      render: (value) => <Chip size="small" color="primary" label={String(value)} />,
    },
  ];

  // Column definitions for order details
  const orderColumns = [
    {
      key: 'orderNumber',
      label: 'Order #',
      render: (value) => (
        <Typography component="span" variant="body2" fontWeight={600}>
          {value}
        </Typography>
      ),
    },
    {
      key: 'customerName',
      label: 'Customer'
    },
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'date'
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'date'
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      render: (value) => {
        const c = statusColor(value);
        return <Chip size="small" label={String(value || '—')} color={c} variant="outlined" />;
      },
    },
  ];

  const handleRowClick = (row) => {
    navigate(`/orders/${row.uuid}`);
  };

  const dateInputValue = date
    ? (() => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      })()
    : '';

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mt: 1 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Items needed by date
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        See demand for an event day and open related orders
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          type="date"
          label="Date"
          size="small"
          value={dateInputValue}
          onChange={handleDateChange}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: { sm: 220 } }}
        />
        <Button variant="contained" onClick={() => fetchItems(date)} disabled={!date || loading} sx={{ alignSelf: { sm: 'center' } }}>
          {loading ? 'Checking…' : 'Check items'}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {!loading && !error && items.length > 0 && (
        <Stack spacing={3} sx={{ mt: 1 }}>
          <RelatedItems
            title="Inventory summary"
            data={items}
            columns={inventoryColumns}
            emptyMessage="No items needed"
            collapsible={false}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              Orders by item
            </Typography>
            <Stack spacing={2}>
              {items.map((item) => (
                <RelatedItems
                  key={item.inventoryId}
                  title={`${item.name} — orders`}
                  data={item.orders || []}
                  columns={orderColumns}
                  emptyMessage="No orders for this item"
                  collapsible
                  defaultExpanded={false}
                  onRowClick={handleRowClick}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      )}

      {!loading && !error && items.length === 0 && date && (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            px: 2,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography color="text.secondary" variant="body1">
            No items needed for this date
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
            Try a different day
          </Typography>
        </Box>
      )}

      {!date && !loading && (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography color="text.secondary" variant="body1">
            Select a date to check items needed
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ItemsNeededByDate;